import { Outlet, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { useMatch } from "react-router-dom";
import { Icon } from "@/components/ui/icon";
import { IconButton } from "@/components/common/icon-button";
import { useLogout } from "@/hooks/use-logout";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { AccountIcon, LogoutIcon, MembersIcon } from "./assets";
import { iamAccountHooks } from "@/manager";
import { SubscriptionIcon } from "@/components/settings/assets";

interface LogoutComponentProps {
  name: string;
  email: string;
  onLogout: () => void;
}

function AccountInfo({
  name,
  email,
  onLogout,
}: LogoutComponentProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start justify-between mx-1 mb-5">
      {/* 用户信息区域 */}
      <div className="flex items-center space-x-3">
        {/* 用户名和邮箱 */}
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[rgba(16,24,40,1)]">
            {name}
          </span>
          <span className="text-xs text-[rgba(71,84,103,1)]">{email}</span>
        </div>
      </div>

      {/* 退出登录按钮 */}
      <IconButton
        icon={<LogoutIcon className="w-5 h-5 text-[rgb(102,112,133)]" />}
        title={t("settings.index.logout")}
        onClick={onLogout}
      />
    </div>
  );
}

interface MenuOptionsProps {
  options: {
    id: string;
    active: boolean;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
  }[];
}

export function MenuOptions({ options }: MenuOptionsProps) {
  const { t } = useTranslation();
  return (
    <div>
      <div className={cn("group flex items-center justify-between mb-3")}>
        <h5 className={cn("text-sm font-normal text-i-sub")}>
          {t("settings.index.settings_and_members")}
        </h5>
      </div>
      {options.map((option) => (
        <div key={option.id}>
          <div
            className={cn(
              "group flex items-center justify-between my-1 pl-3 pr-2.5 h-10 font-medium text-base rounded-sm cursor-pointer",
              option.active
                ? "text-i-active bg-i-active"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
            onClick={option.onClick}
          >
            <div className="flex items-center gap-3 flex-1">
              {option.icon && (
                <span className="shrink-0">{option.icon}</span>
              )}
              <span>{option.label}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsSidebar({ className }: { className?: string }) {
  const navigate = useI18nNavigate();
  const { account } = iamAccountHooks.useData();
  const { handleLogout } = useLogout();
  const isAccount = useMatch("/settings/account/");
  const isMembers = useMatch("/settings/members/");
  const isSubscription = useMatch("/settings/subscription/");
  const { t } = useTranslation();

  const menuOptions = [
    {
      id: "0",
      label: t("settings.index.account"),
      icon: <AccountIcon className="w-5 h-5" />,
      active: Boolean(isAccount),
      onClick: () => navigate("/settings/account/"),
    },
    // {
    //   id: "2",
    //   label: t("settings.index.subscription"),
    //   icon: <SubscriptionIcon className="w-5 h-5" />,
    //   active: Boolean(isSubscription),
    //   onClick: () => navigate("/settings/subscription/"),
    // },
    // {
    //   id: "1",
    //   label: t("settings.index.members"),
    //   icon: <MembersIcon className="w-5 h-5" />,
    //   active: Boolean(isMembers),
    //   onClick: () => navigate("/settings/members/"),
    // },
  ];

  return (
    <aside
      className={cn(
        "flex flex-col px-4 w-72 h-full overflow-y-auto",
        "bg-sidebar-background border-r border-sidebar-border",
        "shadow-soft",
        className
      )}
    >
      <Icon className="mr-auto my-7 h-10" />
      <MenuOptions options={menuOptions} />
      {account && (
        <div className="mt-auto">
          <AccountInfo
            name={account.username}
            email={account.email}
            onLogout={handleLogout}
          />
        </div>
      )}
    </aside>
  );
}


export function Settings() {
  const context = useOutletContext<{ collapse: boolean }>();
  const { collapse } = context;
  return (
    <>
      {/* 二级侧边栏 */}
      <SettingsSidebar
        className={cn(
          "w-72 whitespace-nowrap transition-all duration-200",
          collapse && "-translate-x-full p-0 w-0 opacity-0 overflow-hidden"
        )}
      />
      {/* 右侧内容区 */}
      <main className={cn("flex-1 bg-gray-50")}>
        <Outlet />
      </main>
    </>
  );
}
